import React, { useEffect, useMemo, useState } from "react";
import { listScenarios, getScenario, deleteScenario } from "../../api";

const CARD = "rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-5";
const LBL = "text-xs text-slate-400";
const H2 = "text-sm font-semibold tracking-wider text-slate-200";
const INP =
    "w-full rounded-xl border border-[#1f2638] bg-[#0f1523] px-3 py-2 text-sm text-slate-200 placeholder:text-slate-500";

const BTN_GHOST =
    "inline-flex items-center justify-center rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-semibold hover:bg-[#0e1320] disabled:opacity-50 disabled:cursor-not-allowed";

const BTN =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-[#1f2638] bg-[#121825] px-4 py-2 text-sm font-semibold hover:bg-[#141b2d] disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_PRIMARY =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 disabled:opacity-60 disabled:cursor-not-allowed";

const BTN_DANGER =
    "inline-flex items-center justify-center gap-2 rounded-xl border border-rose-600/40 bg-rose-600/15 px-4 py-2 text-sm font-semibold hover:bg-rose-600/25 disabled:opacity-60 disabled:cursor-not-allowed";

function dash(v) {
    return v === null || v === undefined || String(v).trim() === "" ? "—" : String(v);
}

function escapeHtml(s = "") {
    return String(s)
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
}

function highlightVars(text = "") {
    const esc = escapeHtml(text);
    const re = /\{\{\s*[^}]+\s*\}\}/g;

    return esc.replace(re, (m) => {
        return `<span class="text-violet-200 bg-violet-600/15 ring-1 ring-violet-500/30 rounded-sm shadow-[0_0_12px_rgba(139,92,246,0.35)] [box-decoration-break:clone] [-webkit-box-decoration-break:clone]">${m}</span>`;
    });
}

export default function ScenariosView({ onBack, onAdd, onEdit }) {
    const [q, setQ] = useState("");

    const [items, setItems] = useState([]);
    const [loadingList, setLoadingList] = useState(false);

    const [selectedId, setSelectedId] = useState("");
    const [selected, setSelected] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [err, setErr] = useState("");
    const [toast, setToast] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 2200);
        return () => clearTimeout(t);
    }, [toast]);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            try {
                setErr("");
                setLoadingList(true);
                const arr = await listScenarios();

                if (!alive) return;

                setItems(arr || []);
                setSelectedId((prev) => {
                    const keep = prev && (arr || []).some((x) => x?.id === prev);
                    return keep ? prev : (arr?.[0]?.id || "");
                });
            } catch (e) {
                if (!alive) return;
                setErr(e?.message || "Грешка при зареждане");
                setItems([]);
                setSelectedId("");
            } finally {
                if (alive) setLoadingList(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [reloadKey]);

    useEffect(() => {
        let alive = true;

        const run = async () => {
            if (!selectedId) {
                setSelected(null);
                return;
            }
            try {
                setErr("");
                setLoadingDetail(true);
                const data = await getScenario(selectedId);
                if (!alive) return;
                setSelected(data);
            } catch (e) {
                if (!alive) return;
                setErr(e?.message || "Грешка при детайл");
                setSelected(null);
            } finally {
                if (alive) setLoadingDetail(false);
            }
        };

        run();
        return () => {
            alive = false;
        };
    }, [selectedId]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return items;
        return (items || []).filter((x) => {
            const name = (x?.name || "").toLowerCase();
            return name.includes(s);
        });
    }, [items, q]);

    const promptHtml = useMemo(() => {
        const raw = selected?.prompt ?? "";
        return highlightVars(String(raw));
    }, [selected?.prompt]);

    const firstMessageText = useMemo(() => {
        const raw = selected?.first_message ?? selected?.firstMessage ?? "";
        return String(raw || "");
    }, [selected?.first_message, selected?.firstMessage]);

    const doDelete = async () => {
        const id = selected?.id || selectedId;
        if (!id) return;

        const ok = window.confirm("Сигурен ли си, че искаш да изтриеш този сценарий?");
        if (!ok) return;

        try {
            setDeleting(true);
            setErr("");
            await deleteScenario(id);
            setToast("Изтрито");
            setSelectedId("");
            setSelected(null);
            setReloadKey((k) => k + 1);
        } catch (e) {
            setErr(e?.message || "Грешка при изтриване");
            setToast(e?.message || "Грешка при изтриване");
            setReloadKey((k) => k + 1);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl font-bold">Сценарии</h1>

                <div className="flex-1" />

                <button className={BTN_PRIMARY} onClick={() => onAdd?.()}>
                    Добави сценарий
                </button>

                {typeof onBack === "function" && (
                    <button className={BTN_GHOST} onClick={onBack}>
                        Назад
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <section className={[CARD, "xl:col-span-1"].join(" ")}>
                    <div className={H2}>Списък</div>

                    <div className="mt-4">
                        <input
                            className={INP}
                            placeholder="Търси (name)"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                        />
                    </div>

                    <div className="mt-4 space-y-2">
                        {loadingList ? (
                            <div className="text-sm text-slate-300">Зареждане...</div>
                        ) : err ? (
                            <div className="text-sm text-rose-300">{err}</div>
                        ) : filtered.length === 0 ? (
                            <div className="text-sm text-slate-300">Няма резултати.</div>
                        ) : (
                            filtered.map((x) => {
                                const isSelected = x?.id === selectedId;
                                return (
                                    <button
                                        key={x?.id}
                                        onClick={() => setSelectedId(x?.id)}
                                        className={[
                                            "w-full text-left rounded-2xl border p-4 transition",
                                            isSelected
                                                ? "border-violet-600/50 bg-violet-600/10"
                                                : "border-[#1f2638] bg-[#0f1523] hover:bg-[#121825]",
                                        ].join(" ")}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="font-semibold text-slate-200 truncate">
                                                    {dash(x?.name)}
                                                </div>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })
                        )}
                    </div>
                </section>

                <section className="xl:col-span-2 space-y-6">
                    {!selectedId ? (
                        <div className={CARD}>Няма избран сценарий.</div>
                    ) : loadingDetail && !selected ? (
                        <div className={CARD}>Зареждане на детайл...</div>
                    ) : !selected ? (
                        <div className={CARD}>Няма данни за избрания сценарий.</div>
                    ) : (
                        <div className={CARD}>
                            <div className="flex items-center gap-3">
                                <div className={H2}>Детайли</div>
                                <div className="flex-1" />

                                <button
                                    className={BTN}
                                    onClick={() => onEdit?.(selected?.id)}
                                    disabled={deleting || loadingDetail}
                                >
                                    Промени
                                </button>

                                <button
                                    className={BTN_DANGER}
                                    onClick={doDelete}
                                    disabled={deleting || loadingDetail}
                                >
                                    {deleting ? "Триене..." : "Изтрий"}
                                </button>
                            </div>

                            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div className={LBL}>ID</div>
                                    <div className="text-slate-200">{dash(selected?.id)}</div>
                                </div>

                                <div className="sm:col-span-2">
                                    <div className={LBL}>Име на сценария</div>
                                    <div className="text-slate-200">{dash(selected?.name)}</div>
                                </div>
                            </div>

                            <div className="mt-6">
                                <div className={LBL}>Съобщение след приемане на обаждането</div>
                                <div className="mt-2 rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4 text-sm text-slate-200 whitespace-pre-wrap break-words">
                                    {dash(firstMessageText)}
                                </div>
                            </div>

                            <div className="mt-6">
                                <div className={LBL}>Промпт</div>
                                <pre
                                    className="mt-2 rounded-2xl border border-[#1f2638] bg-[#0f1523] p-4 text-sm text-slate-200 whitespace-pre-wrap break-words overflow-auto max-h-[45vh]"
                                >
                                    <code dangerouslySetInnerHTML={{ __html: promptHtml || "&nbsp;" }} />
                                </pre>
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {toast && (
                <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60]">
                    <div className="rounded-xl border border-slate-800/60 bg-[#0f1523] px-5 py-3 shadow-lg text-sm text-slate-200">
                        {toast}
                    </div>
                </div>
            )}
        </div>
    );
}
